export const tabulateRectangle = (width: number, closed?: boolean) => {
  const xCoords = [];
  const yCoords = [];

  const halfWidth = width / 2;
  const centerX = halfWidth;
  const centerY = width;

  // Top-left corner
  xCoords.push(centerX - halfWidth);
  yCoords.push(centerY + width);

  // Bottom-left corner
  xCoords.push(centerX - halfWidth);
  yCoords.push(centerY - width);

  // Bottom-right corner
  xCoords.push(centerX + halfWidth);
  yCoords.push(centerY - width);

  // Top-right corner
  xCoords.push(centerX + halfWidth);
  yCoords.push(centerY + width);

  if (closed) {
    // Top-left corner
    xCoords.push(centerX - halfWidth);
    yCoords.push(centerY + width);
  }

  return [xCoords, yCoords];
};

export const tabulateCircle = (width: number) => {
  const numPoints = 100; // Number of points to generate
  const radius = width / 2; // Radius of the circle
  const x: number[] = [];
  const y: number[] = [];

  for (let i = 0; i < numPoints; i++) {
    const angle = (2 * Math.PI * i) / numPoints;
    x.push(radius + radius * Math.cos(angle));
    y.push(radius + radius * Math.sin(angle)); // Adjust y coordinate by adding the bottom offset
  }
  // Close the circle
  x.push(x[0]);
  y.push(y[0]);

  return [x, y];
};

export const tabulateEgg = (width: number, inverted?: boolean): [number[], number[]] => {
  const numPoints = 100;

  const heightFloat = width * 1.5;
  const position = heightFloat / 3.0; // some parameter for the 'egg' curve
  const heights = Array.from({ length: numPoints }, (_, i) => (heightFloat / (numPoints - 1)) * i);

  // here comes the formulation of the "egg" curve, simplified from inpy
  const h = heightFloat / 2;
  const w = width / 2;
  const a = position / 2;
  const x = heights.map((height) => h - height);
  const p = x.map((xVal) => (h ** 2 - xVal ** 2) * w ** 2);
  const q = x.map((xVal) => h ** 2 + 2 * a * xVal + a ** 2);
  const widths = p.map((pVal, i) => Math.sqrt(pVal / q[i]) * 2);

  let returnHeights = heights;
  if (inverted) returnHeights = returnHeights.reverse();

  return [widths, returnHeights];
};

export const tabulateTrapezium = (
  [widthArray, heightArray]: [number[], number[]],
  rectangle?: boolean
) => {
  // If its a rectangle trapezium double the x and add the next y to the y array.
  widthArray = widthArray.flatMap((x, index) =>
    !rectangle || index === widthArray.length - 1 ? x : [x, x]
  );
  heightArray = heightArray.flatMap((y, index) =>
    !rectangle || index === heightArray.length - 1 ? y : [y, heightArray[index + 1] ?? y]
  );

  const maxWidth = Math.max(...widthArray);

  // Copy a reverse of the array with negative half width and a copy of the array the right way to create a trapezium
  const returnThis = [
    [
      ...[...widthArray].reverse().map((width) => maxWidth / 2 + -width / 2),
      ...widthArray.map((width) => maxWidth / 2 + width / 2),
    ],
    [...[...heightArray].reverse(), ...heightArray],
  ];

  // If the top width === 0 the trapezium is closed so so we close it
  const topWidth = widthArray[widthArray.length - 1];
  if (topWidth <= 0) {
    returnThis[0].push(maxWidth / 2 + -topWidth / 2);
    returnThis[1].push(heightArray[heightArray.length - 1]);
  }
  return returnThis;
};

export const getBottom = (crossSectionProperties: any) =>
  crossSectionProperties["crest_level"] ||
  crossSectionProperties["reference_level"] ||
  (crossSectionProperties["invert_level_start_point"] &&
    crossSectionProperties["invert_level_end_point"] &&
    Math.max(
      crossSectionProperties["invert_level_start_point"],
      crossSectionProperties["invert_level_end_point"]
    ));

const roundDown = (num: number, decimales = 2) =>
  Math.floor(num * 10 ** decimales) / 10 ** decimales;

export const scatterData = (x: number[], y: number[], bottom: number) =>
  ({
    x,
    y: y.map((yNum) => yNum + bottom),
    hovertemplate: "%{text}, %{y:.2f}",
    text: y.map((yNum) => {
      let lowWidth: number | null = null;
      let width: number | null = null;
      y.find((yInNum, inIndex) => {
        const sameY = roundDown(yNum, 4) === roundDown(yInNum, 4);
        if (!sameY) return false;
        const newWidth = x[inIndex];
        if (lowWidth === null || width === null) {
          lowWidth = newWidth;
          width = newWidth;
          return false;
        }
        if (newWidth > lowWidth) {
          width = newWidth;
          return true;
        }
        if (newWidth < lowWidth) {
          lowWidth = newWidth;
          return true;
        }
      });

      if (lowWidth === null || width === null) return null;
      return roundDown(width - lowWidth) + "";
    }),
    type: "scatter",
    mode: "lines",
    fillcolor: "#4287f5",
    showlegend: false,
    hoverinfo: "skip",
    marker: {
      color: "#4287f5",
    },
  } as Plotly.ScatterData);
